import confetti from 'canvas-confetti';
import { useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { SUITS, SUIT_NAME, SUIT_SYMBOL, TRICKS_PER_DEAL, matchStatus, nextSeat, otherTeam, scheduledDealCount, teamOfSeat, totals, type MatchState, type Seat, type Suit, type TeamId } from '../../game/match';
import type { BandhDeal } from '../../game/bandh';
import { FIRST_PACKET, HIDDEN_CARD, RUN_FROM_TRICK, canCallOff, cardSuit, dealSequence, rangTeam, runChance, sortHand, type Card, type HandView, type Trick } from '../../game/rang';
import { confirmDialog } from '../../components/Confirm';
import { showGuaaPopup } from '../../components/GuaaPopup';
import { CardBack, PlayingCard, cardName, cardSize } from '../../components/PlayingCard';
import { Avatar, Name, teamAvatarFill } from '../../components/ui';
import { Link } from '../../lib/router';
import type { RoomConnection } from '../../lib/room';
import { tick, unlockAudio } from '../../lib/sound';
import { toast } from '../../lib/toast';
import {
  RANG_HIDDEN,
  RANG_OPENED,
  RUN_PAUSE_MS,
  RUN_STOPPED,
  SEVEN_GUAA_MILESTONE,
  SUIT_HINDI,
  YOUR_TURN,
  announce,
  gameOverPhrase,
  handPhrase,
  isAnnouncing,
  onlineResultSpeech,
  ranPhrase,
  rangPhrase,
} from '../../lib/voice';
import type { RoomView } from '../../shared/room';

type Pos = 'top' | 'right' | 'bottom' | 'left';
const POS: Pos[] = ['top', 'right', 'bottom', 'left'];
/** Where each seat sits on screen, as % of the table box. You are always at the bottom. */
/** Vertical centre of the felt, and its half-width / half-height — all as % of the table box. */
const FELT_Y = 56;
const FELT_RX = 39;
const FELT_RY = 30;
/** Top of the felt (% of the table box); the top seat hangs from here so it stays attached to the table. */
const FELT_TOP = FELT_Y - FELT_RY;

const ANCHOR: Record<Pos, { x: number; y: number }> = {
  top: { x: 50, y: 12 },
  right: { x: 88, y: FELT_Y },
  bottom: { x: 50, y: 94 },
  left: { x: 12, y: FELT_Y },
};
/** Which way each player's card sits from the middle of the felt, and its tilt. */
const SLOT_DIR: Record<Pos, { x: number; y: number; r: number }> = {
  top: { x: 0, y: -1, r: -4 },
  right: { x: 1, y: -0.07, r: 7 },
  bottom: { x: 0, y: 0.93, r: 2 },
  left: { x: -1, y: -0.07, r: -8 },
};
/**
 * Each player's card on the felt (px from its centre), scaled to the felt's size — so a wide,
 * short landscape table spreads the cards sideways instead of pushing them into the seats.
 */
function slotOf(pos: Pos, width: number, height: number) {
  const d = SLOT_DIR[pos];
  return { x: d.x * width * (FELT_RX / 100) * 0.28, y: d.y * height * (FELT_RY / 100) * 0.35, r: d.r };
}

const FLIGHT_MS = 300;
/** How long a finished trick stays face up before it is swept away. */
const HOLD_MS = 1500;

const posOf = (seat: Seat, viewer: Seat): Pos => POS[(seat - viewer + 6) % 4]!;
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface Flight {
  id: number;
  from: Seat;
  to: Seat;
}

// ---------------------------------------------------------------------------
// Animation hooks
// ---------------------------------------------------------------------------

/**
 * Plays the shuffle and the dealing of cards seat by seat whenever the server
 * deals: first 5 to the Rang caller, the rest once Rang is chosen. While it
 * runs, `shown` is how many cards each seat visibly holds.
 */
function useDealAnimation(view: RoomView) {
  const hand = view.hand;
  const [shown, setShown] = useState<number[] | null>(null);
  const [shuffling, setShuffling] = useState(false);
  const [flights, setFlights] = useState<Flight[]>([]);
  const timers = useRef<number[]>([]);
  const seen = useRef<{ handId: number; phase: string } | null>(null);
  const nextId = useRef(0);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    const prev = seen.current;
    seen.current = { handId: view.handId, phase: hand?.phase ?? 'none' };
    // Joined mid-hand (or no hand yet): show the table as it is.
    if (!prev || !hand) return;
    const seq = dealSequence(hand.caller, hand.direction);
    const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms));

    const run = (targets: Seat[], start: number[], step: number, shuffle: boolean) => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      setFlights([]);
      setShown(start);
      if (reduced()) {
        setShown(null);
        return;
      }
      const lead = shuffle ? 1300 : 150;
      setShuffling(shuffle);
      if (shuffle) at(lead - 150, () => setShuffling(false));
      targets.forEach((to, i) => {
        at(lead + i * step, () => {
          const id = ++nextId.current;
          setFlights((f) => [...f, { id, from: hand.dealer, to }]);
          at(FLIGHT_MS, () => {
            setFlights((f) => f.filter((x) => x.id !== id));
            setShown((s) => s && s.map((n, k) => (k === to ? n + 1 : n)));
            tick();
          });
        });
      });
      at(lead + targets.length * step + FLIGHT_MS + 80, () => setShown(null));
    };

    if (view.handId !== prev.handId) {
      if (hand.phase === 'choosingTrump') run(seq.slice(0, FIRST_PACKET), [0, 0, 0, 0], 130, true);
      else run(seq, [0, 0, 0, 0], 45, true);
    } else if (prev.phase === 'choosingTrump' && hand.phase !== 'choosingTrump') {
      run(seq.slice(FIRST_PACKET), [0, 1, 2, 3].map((s) => (s === hand.caller ? FIRST_PACKET : 0)), 55, false);
    }
    // Only deal transitions matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.handId, hand?.phase]);

  return { shown, shuffling, flights, dealing: shown !== null || shuffling };
}

/** Keeps a just-finished trick face up for a moment so everyone sees who won it. */
function useHeldTrick(hand: HandView | null) {
  const [held, setHeld] = useState<Trick | null>(null);
  const count = hand?.tricks.length ?? 0;
  const prev = useRef<number | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = count;
    if (before === null || !hand) return;
    if (count < before) setHeld(null);
    if (count <= before) return;
    setHeld(hand.tricks.at(-1)!);
    const t = window.setTimeout(() => setHeld(null), HOLD_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);
  return held;
}

/** When everyone shares one room, only one phone reads announcements aloud. */
const speaksHere = (view: RoomView) => !view.sameRoom || view.speaker === view.you;

/** Big "Rang is ♠ Spades" banner for everyone the moment the caller chooses. */
function useTrumpReveal(view: RoomView) {
  const [suit, setSuit] = useState<Suit | null>(null);
  const seen = useRef<string | null>(null);
  const bandh = view.variant === 'bandh';
  // Bandh Rang: the chooser knows the suit early, but it's only "revealed" (and said aloud) once opened.
  const trump = bandh ? (view.hand?.rangRevealed ? view.hand.trump : null) : (view.hand?.trump ?? null);
  useEffect(() => {
    const key = `${view.handId}:${trump}`;
    const before = seen.current;
    seen.current = key;
    if (before === null || !trump || before === key || !before.startsWith(`${view.handId}:`)) return;
    setSuit(trump);
    if (speaksHere(view)) announce(bandh ? [RANG_OPENED, rangPhrase(trump)] : [rangPhrase(trump)]);
    const t = window.setTimeout(() => setSuit(null), 2600);
    return () => clearTimeout(t);
  }, [view.handId, trump]);

  // Bandh Rang: once the hidden Rang is chosen, every phone hears "Rang band hai".
  const phase = view.hand?.phase;
  const lastPhase = useRef<string | null>(null);
  useEffect(() => {
    const key = `${view.handId}:${phase}`;
    const before = lastPhase.current;
    lastPhase.current = key;
    if (!bandh || before !== `${view.handId}:choosingTrump` || phase !== 'playing') return;
    if (speaksHere(view)) announce([RANG_HIDDEN]);
  }, [view.handId, phase]);
  return suit;
}

/** "Aapki baari hai" on your own phone when it becomes your turn (unless something else is being said). */
function useYourTurnNudge(myTurn: boolean) {
  useEffect(() => {
    if (!myTurn) return;
    const t = window.setTimeout(() => {
      if (isAnnouncing()) return;
      // Only this phone says it, so there's nothing to echo — leave the mic on.
      announce([YOUR_TURN], { pauseMic: false });
      navigator.vibrate?.(60);
    }, 500);
    return () => clearTimeout(t);
  }, [myTurn]);
}

/** Tells every phone why the cards are being dealt again. */
function useRedealNotice(view: RoomView) {
  const redeals = view.hand?.redeals ?? 0;
  const seen = useRef<string | null>(null);
  useEffect(() => {
    const key = `${view.deals.length}:${redeals}`;
    const before = seen.current;
    seen.current = key;
    if (before === null || before === key || redeals === 0 || !view.hand) return;
    const { caller, dealer } = view.hand;
    toast(`🔄 ${view.seats[caller]!.name} had no card above 9 — ${view.seats[dealer]!.name} is dealing again.`, 'info', 5000);
  }, [view.deals.length, redeals, view.hand, view.seats]);
}

interface SavedCelebration {
  /** The runner, the last-hand winner, or whoever called the deal off. */
  hero: Seat;
  team: { name: string; color: string };
  by: 'run' | 'lastTrick' | 'calledOff';
  atTrick: number;
  key: number;
}

/** The Rang team saved itself: a celebration to match the Guaa popup. Stays until tapped. */
const SAVED_POPUP_MS = 3000;

function SavedPopup({ view, c, onClose }: { view: RoomView; c: SavedCelebration; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const hero = view.seats[c.hero]!;
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // Drawn inside the popup: anything outside sits under its backdrop.
    if (canvas.current) {
      const burst = confetti.create(canvas.current, { resize: true });
      void burst({ particleCount: 140, spread: 85, origin: { y: 0.35 }, colors: [c.team.color, '#d4a84b', '#f7f1e3', '#22c55e'], disableForReducedMotion: true });
    }
    // A save is good news, not a decision: it clears itself after a few seconds (a tap closes it sooner).
    const t = window.setTimeout(onClose, SAVED_POPUP_MS);
    return () => {
      clearTimeout(t);
      d?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const headline = c.by === 'run' ? `${c.team.name} bhaag gaya!` : c.by === 'lastTrick' ? 'Saved on the last hand!' : 'Deal called off';
  const detail =
    c.by === 'run'
      ? `${hero.name} ran on hand ${c.atTrick} — two in a row.`
      : c.by === 'lastTrick'
        ? `No run, but ${hero.name} took hand 13.`
        : `${hero.name} called off the deal.`;
  return (
    <dialog
      ref={ref}
      aria-labelledby="saved-h"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => e.target === ref.current && onClose()}
      className="m-auto w-[min(22rem,calc(100%-2.5rem))] max-w-none rounded-[2rem] bg-transparent p-0 backdrop:bg-black/60 backdrop:backdrop-blur-[2px]"
    >
      <canvas ref={canvas} className="pointer-events-none fixed inset-0 z-10 h-full w-full" aria-hidden />
      <div
        className="animate-pop relative overflow-hidden rounded-[2rem] text-center text-white shadow-[0_30px_70px_-20px_rgb(0_0_0/0.8)] ring-2 ring-white/20"
        style={{ background: 'linear-gradient(165deg, #22c55e 0%, #15803d 50%, #0b3d24 100%)' }}
      >
        {/* Shrinks to nothing as the popup's 3 seconds run out. */}
        <span
          className="absolute inset-x-0 bottom-0 h-1 origin-left bg-white/60"
          style={{ animation: `shrink-x ${SAVED_POPUP_MS}ms linear forwards` }}
          aria-hidden
        />
        <div className="px-6 pt-7 pb-6">
          <span className="animate-guaa-stamp inline-block rounded-lg bg-white px-4 py-1 text-xl font-extrabold tracking-wider text-success-700 uppercase shadow-lg">
            Saved!
          </span>
          <div className="relative mx-auto mt-5 w-fit">
            <span className="pointer-events-none absolute -inset-3 rounded-full bg-gold/30 blur-xl" aria-hidden />
            <span className="relative block">
              <Avatar name={hero.name} avatarKey={hero.avatarKey} size={104} ring="#fff" fill={teamAvatarFill(c.team.color)} />
            </span>
            <span className="absolute -top-2 -right-3 text-4xl drop-shadow" aria-hidden>
              {c.by === 'calledOff' ? '🏳️' : '🏃'}
            </span>
            <span className="absolute -bottom-1 -left-4 text-3xl drop-shadow" aria-hidden>
              🎉
            </span>
          </div>
          <h2 id="saved-h" className="mt-4 font-display text-3xl leading-tight font-extrabold">
            {headline}
          </h2>
          <p className="mt-1 text-sm text-white/85">{detail}</p>
          <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-black/20 px-4 py-1.5 font-display text-lg font-extrabold">
            <span style={{ color: '#fff' }}>{c.team.name}</span> · 0 Guaa
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-5 block w-full rounded-xl bg-white/15 py-2.5 text-sm font-semibold text-white ring-1 ring-white/25 hover:bg-white/25"
          >
            Tap to continue
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** Popup, confetti and the Hindi announcement when a hand is decided. */
function useHandResult(view: RoomView, state: MatchState, onSaved: (c: SavedCelebration) => void, onBandh: (r: BandhResult) => void) {
  const hand = view.hand;
  const decided = hand?.outcome ? view.handId : null;
  const seen = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const before = seen.current;
    seen.current = decided;
    if (before === undefined || decided === null || before === decided || !hand?.outcome) return;
    if (view.bandh) {
      const b = view.bandh;
      const last = b.deals.at(-1);
      if (!last) return;
      const t = window.setTimeout(() => {
        onBandh({ deal: last, loser: b.loser, key: view.handId });
        if (!speaksHere(view)) return;
        const who = view.seats[last.by]!.name;
        const next = view.nextDealer !== null ? [{ pauseMs: RUN_PAUSE_MS }, handPhrase(view.seats[view.nextDealer]!.name, 1, 1)] : [];
        const over = b.loser ? [{ pauseMs: RUN_PAUSE_MS }, gameOverPhrase(view.teams[b.loser].name)] : [];
        announce(last.result === 'run' ? [ranPhrase(who), ...over, ...next] : [RUN_STOPPED, ...over, ...next]);
      }, HOLD_MS);
      return () => clearTimeout(t);
    }
    const t = window.setTimeout(() => {
      const caller = view.seats[hand.caller]!;
      const teamId = rangTeam(hand);
      const team = view.teams[teamId];
      const last = view.deals.at(-1);
      if (hand.outcome!.saved) {
        const { by, decidedBy, atTrick } = hand.outcome!;
        onSaved({ hero: decidedBy, team, by, atTrick, key: view.handId });
      } else if (last) {
        const t = totals(view.deals);
        const teamTotal = teamId === 'A' ? t.guaaA : t.guaaB;
        showGuaaPopup({
          name: caller.name,
          avatarKey: caller.avatarKey,
          team,
          guaa: last.guaaA + last.guaaB,
          isCourt: last.isCourt,
          milestone: teamTotal === SEVEN_GUAA_MILESTONE ? teamTotal : null,
        });
      }
      const { by, decidedBy } = hand.outcome!;
      if (speaksHere(view)) announce(onlineResultSpeech(state, view.seats, view.teams, by === 'run' ? view.seats[decidedBy]!.name : null));
    }, HOLD_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decided]);
}

function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 360, height: 400 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ width: e!.contentRect.width, height: e!.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size] as const;
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function FlyingCard({ from, to }: { from: Pos; to: Pos }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    ref.current?.animate(
      [
        { left: `${ANCHOR[from].x}%`, top: `${ANCHOR[from].y}%`, transform: 'translate(-50%,-50%) rotate(-25deg) scale(0.55)' },
        { left: `${ANCHOR[to].x}%`, top: `${ANCHOR[to].y}%`, transform: 'translate(-50%,-50%) rotate(10deg) scale(0.7)' },
      ],
      { duration: FLIGHT_MS, easing: 'cubic-bezier(.25,.8,.35,1)', fill: 'forwards' },
    );
  }, [from, to]);
  return (
    <span ref={ref} className="pointer-events-none absolute z-30" style={{ left: `${ANCHOR[from].x}%`, top: `${ANCHOR[from].y}%` }} aria-hidden>
      <CardBack size="sm" />
    </span>
  );
}

/** A card on the felt. `lead` marks the card that started the trick (the suit everyone must follow). */
function TrickCard({
  card,
  pos,
  trump,
  winning,
  lead,
  size,
  faceDown = false,
}: {
  card: Card;
  pos: Pos;
  trump: Suit | null;
  winning: boolean;
  lead: boolean;
  size: { width: number; height: number };
  /** Bandh Rang discard played face down (others get HIDDEN_CARD; the owner sees it, tagged). */
  faceDown?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const slot = slotOf(pos, size.width, size.height);
  useLayoutEffect(() => {
    if (reduced()) return;
    ref.current?.animate(
      [
        { left: `${ANCHOR[pos].x}%`, top: `${ANCHOR[pos].y}%`, opacity: 0.4, transform: `translate(-50%,-50%) rotate(${slot.r * 4}deg) scale(0.8)` },
        { left: `calc(50% + ${slot.x}px)`, top: `calc(${FELT_Y}% + ${slot.y}px)`, opacity: 1, transform: `translate(-50%,-50%) rotate(${slot.r}deg) scale(1)` },
      ],
      { duration: 280, easing: 'cubic-bezier(.2,.9,.3,1.15)' },
    );
    // Animate once, when the card lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <span
      ref={ref}
      className={`absolute transition-transform duration-300 ${winning ? 'z-30' : 'z-20'}`}
      style={{
        left: `calc(50% + ${slot.x}px)`,
        top: `calc(${FELT_Y}% + ${slot.y}px)`,
        transform: `translate(-50%,-50%) rotate(${slot.r}deg) scale(${winning ? 1.1 : lead ? 1.05 : 1})`,
      }}
    >
      {card === HIDDEN_CARD ? (
        <span role="img" aria-label="A card played face down" className="block">
          <CardBack size="sm" />
        </span>
      ) : (
      <PlayingCard
        card={card}
        size="sm"
        trump={cardSuit(card) === trump}
        style={
          winning
            ? { boxShadow: '0 0 0 3px #ecd48f, 0 0 22px rgb(236 212 143 / 0.9)' }
            : lead
              ? { boxShadow: '0 0 0 2.5px #d4a84b, 0 0 0 5px rgb(212 168 75 / 0.28), 0 0 18px rgb(236 212 143 / 0.55), 0 8px 16px -6px rgb(0 0 0 / 0.6)' }
              : undefined
        }
      />
      )}
      {faceDown && card !== HIDDEN_CARD && (
        <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-felt-950 px-1.5 py-px text-[8px] font-extrabold tracking-wider whitespace-nowrap text-cream/90 ring-1 ring-white/20" aria-hidden>
          FACE DOWN
        </span>
      )}
      {lead && (
        <span
          className={`absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-gold px-1.5 py-px text-[8px] font-extrabold tracking-wider whitespace-nowrap shadow ring-1 ring-felt-950 ${
            cardSuit(card) === 'H' || cardSuit(card) === 'D' ? 'text-suit-red' : 'text-ink'
          }`}
          aria-hidden
        >
          <span className="text-ink">PLAY CARD</span> {SUIT_SYMBOL[cardSuit(card)]}
        </span>
      )}
    </span>
  );
}

/**
 * A short gold curve from the player whose turn it is to the spot on the felt
 * where their card will land, with a dashed outline of that spot.
 */
function TurnArrow({ pos, width, height }: { pos: Pos; width: number; height: number }) {
  const ax = (ANCHOR[pos].x / 100) * width;
  const ay = (ANCHOR[pos].y / 100) * height;
  // Leave from the edge of the player's avatar (the top seat hangs below its avatar, name and cards).
  const start =
    pos === 'top' ? { x: width / 2, y: ((FELT_TOP + 4) / 100) * height } : pos === 'bottom' ? { x: width / 2, y: ay - 24 } : { x: ax + (pos === 'left' ? 30 : -30), y: ay - 26 };
  const off = slotOf(pos, width, height);
  const slot = { x: width / 2 + off.x, y: (FELT_Y / 100) * height + off.y };
  const dx = slot.x - start.x;
  const dy = slot.y - start.y;
  const len = Math.hypot(dx, dy) || 1;
  // Stop just short of the card outline.
  const end = { x: slot.x - (dx / len) * 40, y: slot.y - (dy / len) * 40 };
  const bow = Math.min(22, len / 3);
  const ctrl = { x: (start.x + end.x) / 2 - (dy / len) * bow, y: (start.y + end.y) / 2 + (dx / len) * bow };
  const d = `M ${start.x} ${start.y} Q ${ctrl.x} ${ctrl.y} ${end.x} ${end.y}`;
  return (
    <>
      <span
        className="pointer-events-none absolute z-[5] h-[66px] w-[46px] -translate-x-1/2 -translate-y-1/2 rounded-[9px] border-2 border-dashed border-gold-soft/70 bg-gold-soft/10"
        style={{ left: slot.x, top: slot.y, rotate: `${off.r}deg` }}
        aria-hidden
      />
      <svg className="pointer-events-none absolute inset-0 z-[15] h-full w-full overflow-visible" viewBox={`0 0 ${width} ${height}`} aria-hidden>
        <defs>
          <marker id="turn-arrow-head" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 Z" fill="#ecd48f" />
          </marker>
        </defs>
        <path d={d} fill="none" stroke="#ecd48f" strokeOpacity="0.3" strokeWidth="3" strokeLinecap="round" />
        <path d={d} fill="none" stroke="#ecd48f" strokeWidth="3" strokeLinecap="round" strokeDasharray="1 7" markerEnd="url(#turn-arrow-head)" />
      </svg>
    </>
  );
}

/**
 * The table top, drawn as SVG ovals (shadow, gold edge, wooden rim, felt, vignette).
 * Plain shapes render the same in every browser — CSS shadows on an oval don't.
 */
function Felt({ width, height, glow = false }: { width: number; height: number; glow?: boolean }) {
  const id = useId().replace(/:/g, '');
  const cx = width / 2;
  const cy = (FELT_Y / 100) * height;
  const rx = width * (FELT_RX / 100);
  const ry = height * (FELT_RY / 100);
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <defs>
        <radialGradient id={`${id}-shadow`}>
          <stop offset="70%" stopColor="#000" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-wood`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" stopColor="#8a5a2b" />
          <stop offset="45%" stopColor="#5b3a1e" />
          <stop offset="100%" stopColor="#3d2613" />
        </linearGradient>
        <radialGradient id={`${id}-felt`} cx="50%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#2b6b57" />
          <stop offset="70%" stopColor="#123a30" />
        </radialGradient>
        <radialGradient id={`${id}-vignette`}>
          <stop offset="65%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.55" />
        </radialGradient>
      </defs>
      <ellipse cx={cx} cy={cy + 14} rx={rx + 10} ry={ry + 10} fill={`url(#${id}-shadow)`} />
      <ellipse cx={cx} cy={cy} rx={rx + 1.5} ry={ry + 1.5} fill="rgb(236 212 143 / 0.45)" />
      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={`url(#${id}-wood)`} />
      <ellipse cx={cx} cy={cy} rx={rx - 6} ry={ry - 6} fill={`url(#${id}-felt)`} />
      <ellipse cx={cx} cy={cy} rx={rx - 6} ry={ry - 6} fill={`url(#${id}-vignette)`} />
      {/* A dragged card is over the table: light up the felt. */}
      {glow && <ellipse cx={cx} cy={cy} rx={rx - 9} ry={ry - 9} fill="rgb(236 212 143 / 0.08)" stroke="#ecd48f" strokeWidth="3" strokeDasharray="10 8" className="animate-deal-flow" />}
    </svg>
  );
}

/** Which hand of the 13 is being played, as a tile in the table's top-left corner (mirrors the Rang tile). */
function HandCountTile({ hand, trickNo }: { hand: HandView; trickNo: number }) {
  if (hand.phase !== 'playing' && hand.phase !== 'done') return null;
  const decided = hand.phase === 'done' && hand.outcome;
  // The hand whose cards are on the felt (a just-won one stays counted while it's shown).
  const n = decided ? Math.max(hand.outcome!.atTrick, 1) : Math.min(trickNo + 1, TRICKS_PER_DEAL);
  const ord = (k: number) => (k % 100 >= 11 && k % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][k % 10] ?? 'th');
  return (
    <div
      className="absolute top-2 left-1 z-20 flex w-[4.25rem] flex-col items-center rounded-xl bg-felt-950/75 px-1 pt-1 pb-1.5 text-center text-cream ring-2 ring-gold/40"
      role="status"
      aria-label={decided ? `Decided on hand ${n} of ${TRICKS_PER_DEAL}` : `Hand ${n} of ${TRICKS_PER_DEAL} in progress`}
    >
      <span className="text-[9px] font-extrabold tracking-[0.18em] text-gold-soft/80">HAND</span>
      <span className="font-display text-[1.75rem] leading-none font-extrabold tabular-nums">
        {n}
        <span className="align-top text-xs font-bold">{ord(n)}</span>
      </span>
      <span className="mt-0.5 text-[9px] font-semibold text-cream/70">{decided ? 'decided' : 'in progress'}</span>
    </div>
  );
}

/** The Rang suit as a little card in the table's top-right corner, where every player can see it. */
function RangTile({ hand, name, peek }: { hand: HandView; name: string; peek: Suit | null }) {
  const suit = hand.trump;
  // Bandh Rang, chosen but not revealed: a lock for everyone. The chooser can tap to peek for a moment.
  const locked = Boolean(hand.hidden && !hand.rangRevealed && hand.phase !== 'choosingTrump');
  const [peeking, setPeeking] = useState(false);
  useEffect(() => {
    if (!peeking) return;
    const t = window.setTimeout(() => setPeeking(false), 2000);
    return () => clearTimeout(t);
  }, [peeking]);
  if (locked && peek && peeking) {
    const red = peek === 'H' || peek === 'D';
    return (
      <div
        className="absolute top-2 right-1 z-20 flex w-[4.25rem] flex-col items-center rounded-xl bg-cream px-1 pt-1 pb-1.5 text-center text-ink"
        style={{ boxShadow: '0 0 0 2px #d4a84b, 0 10px 24px -10px rgb(0 0 0 / 0.8)' }}
        role="status"
        aria-label={`Your hidden Rang is ${SUIT_NAME[peek]}`}
      >
        <span className="text-[9px] font-extrabold tracking-[0.18em] text-ink-soft">YOUR RANG</span>
        <span className={`text-[2.1rem] leading-none ${red ? 'text-suit-red' : 'text-ink'}`} aria-hidden>
          {SUIT_SYMBOL[peek]}
        </span>
        <span className="mt-0.5 text-[10px] font-bold capitalize">{SUIT_HINDI[peek].roman}</span>
      </div>
    );
  }
  if (locked && !suit) {
    return (
      <div
        className="animate-pop absolute top-2 right-1 z-20 flex w-[4.25rem] flex-col items-center rounded-xl px-1 pt-1 pb-1.5 text-center text-cream"
        style={{ background: 'linear-gradient(160deg, #3b2f63 0%, #241c40 100%)', boxShadow: '0 0 0 2px rgb(212 168 75 / 0.6), 0 10px 24px -10px rgb(0 0 0 / 0.8)' }}
        aria-label={`Rang is hidden — ${name} chose it`}
        role="status"
      >
        <span className="text-[9px] font-extrabold tracking-[0.18em] text-gold-soft/80">RANG</span>
        <span className="text-[1.9rem] leading-none" aria-hidden>
          🔒
        </span>
        <span className="mt-0.5 text-[10px] font-bold">Hidden</span>
        {peek && (
          <button
            type="button"
            onClick={() => setPeeking(true)}
            className="mt-1 rounded-full bg-white/15 px-1.5 py-0.5 text-[9px] font-bold text-gold-soft ring-1 ring-gold/40 hover:bg-white/25"
            aria-label="Peek at your hidden Rang (only on your screen)"
          >
            Tap to peek
          </button>
        )}
      </div>
    );
  }
  const red = suit === 'H' || suit === 'D';
  return (
    <div
      className="animate-pop absolute top-2 right-1 z-20 flex w-[4.25rem] flex-col items-center rounded-xl px-1 pt-1 pb-1.5 text-center text-ink shadow-[0_10px_24px_-10px_rgb(0_0_0/0.8)]"
      style={{
        background: 'linear-gradient(160deg, #fffdf7 0%, #f7f1e3 60%, #ece2c9 100%)',
        boxShadow: suit ? '0 0 0 2px #d4a84b, 0 0 18px rgb(236 212 143 / 0.45), 0 10px 24px -10px rgb(0 0 0 / 0.8)' : '0 0 0 2px rgb(212 168 75 / 0.5)',
      }}
      aria-label={suit ? `Rang is ${SUIT_NAME[suit]} (${SUIT_HINDI[suit].roman}), chosen by ${name}` : `${name} is choosing Rang`}
      role="status"
    >
      <span className="text-[9px] font-extrabold tracking-[0.18em] text-ink-soft">RANG</span>
      {suit ? (
        <>
          <span className={`text-[2.1rem] leading-none ${red ? 'text-suit-red' : 'text-ink'}`} aria-hidden>
            {SUIT_SYMBOL[suit]}
          </span>
          <span className="mt-0.5 text-[10px] font-bold capitalize">{locked ? 'only you' : SUIT_HINDI[suit].roman}</span>
        </>
      ) : (
        <>
          <span className="animate-pulse font-display text-[2.1rem] leading-none font-extrabold text-ink-soft/60" aria-hidden>
            ?
          </span>
          <span className="mt-0.5 text-[9px] font-semibold text-ink-soft">choosing</span>
        </>
      )}
    </div>
  );
}

/** Text on the felt: the player's name in the display face, the action as a small gold caps label. */
function FeltCaption({ who, action }: { who?: string; action: string }) {
  return (
    <p className="leading-tight [text-shadow:0_2px_10px_rgb(0_0_0/0.55)]">
      {who && (
        <Name className="block truncate font-display text-[1.35rem] font-extrabold tracking-tight text-cream">{who}</Name>
      )}
      <span className={`block text-[10px] font-extrabold tracking-[0.22em] text-gold-soft uppercase ${who ? 'mt-1' : 'font-display text-sm tracking-[0.18em]'}`}>
        {action}
      </span>
    </p>
  );
}

/** A gold arrow near the rim pointing at the player the table is waiting on, nudging toward them. */
function PointerArrow({ pos }: { pos: Pos }) {
  const dir = { top: { x: 0, y: -1, deg: -90 }, right: { x: 1, y: 0, deg: 0 }, bottom: { x: 0, y: 1, deg: 90 }, left: { x: -1, y: 0, deg: 180 } }[pos];
  const left = 50 + dir.x * FELT_RX * 0.64;
  const top = FELT_Y + dir.y * FELT_RY * 0.62;
  return (
    <span
      className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${left}%`, top: `${top}%`, rotate: `${dir.deg}deg` }}
      aria-hidden
    >
      <svg viewBox="0 0 32 24" className="h-6 w-8 drop-shadow-[0_2px_6px_rgb(0_0_0/0.6)]">
        <path d="M2 12h18" stroke="#ecd48f" strokeWidth="3.2" strokeLinecap="round" />
        <path d="M16 3l12 9-12 9z" fill="#ecd48f" />
      </svg>
    </span>
  );
}

function ShuffleDeck() {
  return (
    <span className="absolute left-1/2 z-20 -translate-x-1/2 -translate-y-1/2" style={{ top: `${FELT_Y}%` }} aria-label="Shuffling">
      <span className="relative block h-[66px] w-[46px]">
        <span className="animate-riffle-left absolute inset-0">
          <CardBack size="sm" />
        </span>
        <span className="animate-riffle-right absolute inset-0">
          <CardBack size="sm" />
        </span>
      </span>
    </span>
  );
}

interface SeatProps {
  view: RoomView;
  seat: Seat;
  pos: Pos;
  cards: number;
  turn: boolean;
  run: boolean;
}

/** "RANG ♦" on the caller's avatar — the actual Rang suit, or "?" while they're choosing. */
function RangChipLabel({ hand }: { hand: HandView | null }) {
  const trump = hand?.trump ?? null;
  const locked = Boolean(hand?.hidden && !hand.rangRevealed && hand.phase !== 'choosingTrump');
  return (
    <>
      RANG{' '}
      {locked ? '🔒' : trump ? <span className={trump === 'H' || trump === 'D' ? 'text-suit-red' : ''}>{SUIT_SYMBOL[trump]}</span> : '?'}
    </>
  );
}

function SeatView({ view, seat, pos, cards, turn, run }: SeatProps) {
  const p = view.seats[seat]!;
  const team = view.teams[teamOfSeat(seat)];
  const info = view.seatInfo[seat]!;
  const hand = view.hand;
  const isDealer = (hand?.dealer ?? view.nextDealer) === seat;
  const isCaller = hand?.caller === seat;
  const won = hand?.tricks.filter((t) => t.winner === seat).length ?? 0;
  return (
    <div
      className={`absolute z-10 flex w-[5.5rem] -translate-x-1/2 flex-col items-center gap-1 text-center ${pos === 'top' ? '' : '-translate-y-1/2'} ${info.online ? '' : 'opacity-60'}`}
      style={
        pos === 'top'
          ? // Hang from the felt's edge (just overlapping it) whatever the seat's height.
            { left: `${ANCHOR.top.x}%`, bottom: `${100 - FELT_TOP - 3}%` }
          : { left: `${ANCHOR[pos].x}%`, top: `${ANCHOR[pos].y}%` }
      }
      aria-label={`${p.name}${isDealer ? ', dealer' : ''}${isCaller ? ', chose Rang' : ''}${turn ? ', to play' : ''}, ${cards} cards`}
    >
      <span className="relative">
        <span className={`block rounded-full ${turn ? 'turn-glow' : ''}`}>
          <Avatar name={p.name} avatarKey={p.avatarKey} size={46} ring={team.color} fill={teamAvatarFill(team.color)} />
        </span>
        {info.voice && (
          <span className="absolute -top-1 -right-2 grid h-5 w-5 place-items-center rounded-full bg-felt-950 text-[10px] ring-1 ring-white/20" aria-hidden>
            {info.muted ? '🔇' : '🎙'}
          </span>
        )}
        {run && (
          <span className="absolute -top-2 -left-2 text-base" aria-hidden>
            🔥
          </span>
        )}
        {(isDealer || isCaller) && (
          <span
            className={`absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full px-1.5 py-px text-[8px] font-extrabold tracking-wider whitespace-nowrap shadow ring-1 ring-felt-950 ${
              isDealer ? 'bg-gold text-ink' : 'bg-cream text-ink'
            }`}
          >
            {isDealer ? 'DEALER' : <RangChipLabel hand={hand ?? null} />}
          </span>
        )}
      </span>
      <span className="mt-1 flex max-w-full items-center gap-1">
        <Name className="min-w-0 truncate text-xs font-bold">{p.name}</Name>
        {won > 0 && (
          <span className="shrink-0 rounded-full bg-white/15 px-1.5 text-[10px] font-bold text-cream/90 tabular-nums" title={`${won} tricks won`}>
            {won}
          </span>
        )}
      </span>
      {cards > 0 && (
        <span className="hidden rounded-full bg-felt-950/70 px-1.5 text-[10px] font-bold text-cream/80 ring-1 ring-white/10 tabular-nums land:inline" aria-hidden>
          🂠 {cards}
        </span>
      )}
      {cards > 0 && (
        <span className="flex h-[31px] items-center land:hidden" aria-hidden>
          {Array.from({ length: Math.min(cards, 13) }, (_, i) => (
            <CardBack key={i} size="xs" style={{ marginLeft: i === 0 ? 0 : -17 }} />
          ))}
        </span>
      )}
      {!info.online && info.claimed && <span className="text-[10px] font-semibold text-amber-300">away</span>}
    </div>
  );
}

/** Guaa per team (fewest wins), which deal this is, and the Rang suit. */
function ScoreStrip({ view }: { view: RoomView }) {
  const t = totals(view.deals);
  const playing = view.hand && view.hand.phase !== 'done';
  const dealNo = view.deals.length + (playing ? 1 : 0);
  const leader = t.guaaA < t.guaaB ? 'A' : t.guaaB < t.guaaA ? 'B' : null;
  const side = (id: 'A' | 'B') => {
    const guaa = id === 'A' ? t.guaaA : t.guaaB;
    const right = id === 'B';
    return (
      <span className={`flex min-w-0 flex-1 flex-col gap-0.5 ${right ? 'items-end text-right' : 'items-start'}`}>
        <span className={`flex max-w-full items-center gap-1.5 ${right ? 'flex-row-reverse' : ''}`}>
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: view.teams[id].color }} aria-hidden />
          <span className="min-w-0 truncate text-xs font-bold">{view.teams[id].name}</span>
          {leader === id && (
            <span className="shrink-0 rounded-full bg-success-600 px-1.5 py-px text-[8px] font-extrabold tracking-wider text-white uppercase" title="Fewer Guaa">
              Leading
            </span>
          )}
        </span>
        <span className={`flex items-baseline gap-1 ${right ? 'flex-row-reverse' : ''}`}>
          <span className="font-display text-3xl leading-none font-extrabold tabular-nums" style={{ color: view.teams[id].color }}>
            {guaa}
          </span>
          <span className="text-[9px] font-bold tracking-wider text-ink-soft/80 uppercase">Guaa</span>
        </span>
      </span>
    );
  };
  return (
    <section
      className="card flex items-center gap-2 px-3 py-2"
      aria-label={`Guaa: ${view.teams.A.name} ${t.guaaA}, ${view.teams.B.name} ${t.guaaB}. Fewest Guaa wins.`}
    >
      {side('A')}
      <span className="flex shrink-0 flex-col items-center gap-0.5 px-1 text-center">
        <span className="eyebrow text-[9px] text-ink-soft/70">
          Deal {Math.max(dealNo, 1)}/{scheduledDealCount(view.rules)}
        </span>
        <span className="text-[9px] font-semibold text-ink-soft">Fewest Guaa wins</span>
      </span>
      {side('B')}
    </section>
  );
}

/** +13 / −26 with a real minus sign. */
const signed = (n: number) => (n > 0 ? `+${n}` : `−${Math.abs(n)}`);

/** Bandh Rang: the dealing team runs, the other team stops them. */
function bandhTrackerText(view: RoomView, hand: HandView, runner: Seat | null, k: number): string {
  const running = view.teams[rangTeam(hand)].name;
  const stopping = view.teams[otherTeam(rangTeam(hand))].name;
  if (hand.outcome) {
    const who = view.seats[hand.outcome.decidedBy]!.name;
    return hand.outcome.by === 'run'
      ? `🏃 ${who} ran on hand ${hand.outcome.atTrick} — ${running} −26`
      : `🛡️ ${stopping} stopped the run — ${running} +13`;
  }
  if (hand.phase === 'choosingTrump') return `${view.seats[hand.caller]!.name} is picking a hidden Rang…`;
  if (runner !== null) return `🔥 ${view.seats[runner]!.name} won hand ${k} — win this one and ${running} run! ${stopping}, stop them!`;
  if (k < RUN_FROM_TRICK - 1) return `Hands 1–3 are mandatory. Then ${running} must run — ${stopping} stop them.`;
  return `${running} need a run: one player wins two in a row. No run by hand 13 → +13.`;
}

/** Each team's distance to 100 (the score read from their side), who's dealing, and the latest change. */
function BandhScoreStrip({ view }: { view: RoomView }) {
  const b = view.bandh!;
  const toward = (id: TeamId) => Math.max(0, id === b.owner ? b.score : -b.score);
  const last = b.deals.at(-1);
  const side = (id: TeamId) => {
    const pts = toward(id);
    const right = id === 'B';
    const team = view.teams[id];
    return (
      <span className={`flex min-w-0 flex-1 flex-col gap-1 ${right ? 'items-end text-right' : 'items-start'}`}>
        <span className={`flex max-w-full items-center gap-1.5 ${right ? 'flex-row-reverse' : ''}`}>
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: team.color }} aria-hidden />
          <span className="min-w-0 truncate text-xs font-bold">{team.name}</span>
          {b.dealingTeam === id && !b.loser && (
            <span className="shrink-0 rounded-full bg-gold px-1.5 py-px text-[8px] font-extrabold tracking-wider text-ink uppercase">Dealing</span>
          )}
        </span>
        <span className={`flex items-baseline gap-1 ${right ? 'flex-row-reverse' : ''}`}>
          <span className="font-display text-3xl leading-none font-extrabold tabular-nums" style={{ color: team.color }}>
            {pts}
          </span>
          <span className="text-[9px] font-bold tracking-wider text-ink-soft/80 uppercase">/ {b.limit}</span>
        </span>
        <span className="h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
          <span className={`block h-full rounded-full transition-[width] duration-500 ${right ? 'ml-auto' : ''}`} style={{ width: `${Math.min(100, (pts / b.limit) * 100)}%`, background: team.color }} />
        </span>
      </span>
    );
  };
  return (
    <section
      className="card flex items-center gap-3 px-3 py-2.5"
      aria-label={`Bandh Rang score: ${view.teams.A.name} ${toward('A')} of ${b.limit}, ${view.teams.B.name} ${toward('B')} of ${b.limit}. ${view.teams[b.dealingTeam].name} dealing.`}
    >
      {side('A')}
      <span className="flex shrink-0 flex-col items-center gap-0.5 text-center">
        <span className="eyebrow text-[9px] text-ink-soft/70">Deal {b.deals.length + (view.hand && view.hand.phase !== 'done' ? 1 : 0) || 1}</span>
        {last ? (
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-extrabold tabular-nums ${last.result === 'run' ? 'bg-success-600/15 text-success-700' : 'bg-suit-red/10 text-suit-red'}`}>
            {signed(last.points)} {view.teams[last.runningTeam].name.slice(0, 8)}
          </span>
        ) : (
          <span className="text-[10px] font-semibold text-ink-soft">First to {b.limit} loses</span>
        )}
      </span>
      {side('B')}
    </section>
  );
}

interface BandhResult {
  deal: BandhDeal;
  loser: TeamId | null;
  key: number;
}

/** A run (−26) or a stopped run (+13), shown on every phone. Stays until tapped. */
function BandhResultPopup({ view, r, onClose }: { view: RoomView; r: BandhResult; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const run = r.deal.result === 'run';
  const hero = view.seats[r.deal.by]!;
  const running = view.teams[r.deal.runningTeam];
  const stopping = view.teams[otherTeam(r.deal.runningTeam)];
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    if (canvas.current) {
      const burst = confetti.create(canvas.current, { resize: true });
      void burst({ particleCount: 120, spread: 80, origin: { y: 0.35 }, colors: [(run ? running : stopping).color, '#d4a84b', '#f7f1e3'], disableForReducedMotion: true });
    }
    return () => d?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const b = view.bandh!;
  const after = (id: TeamId) => Math.max(0, id === b.owner ? r.deal.scoreAfter : -r.deal.scoreAfter);
  return (
    <dialog
      ref={ref}
      aria-labelledby="bandh-result-h"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => e.target === ref.current && onClose()}
      className="m-auto w-[min(22rem,calc(100%-2.5rem))] max-w-none rounded-[2rem] bg-transparent p-0 backdrop:bg-black/60 backdrop:backdrop-blur-[2px]"
    >
      <canvas ref={canvas} className="pointer-events-none fixed inset-0 z-10 h-full w-full" aria-hidden />
      <div
        className="animate-pop relative overflow-hidden rounded-[2rem] text-center text-white shadow-[0_30px_70px_-20px_rgb(0_0_0/0.8)] ring-2 ring-white/20"
        style={{ background: run ? 'linear-gradient(165deg, #22c55e 0%, #15803d 50%, #0b3d24 100%)' : 'linear-gradient(165deg, #f43f5e 0%, #9f1239 55%, #3d1020 100%)' }}
      >
        <div className="px-6 pt-7 pb-6">
          <span className={`animate-guaa-stamp inline-block rounded-lg bg-white px-4 py-1 text-xl font-extrabold tracking-wider uppercase shadow-lg ${run ? 'text-success-700' : 'text-suit-red'}`}>
            {run ? 'Ran!' : 'Stopped!'}
          </span>
          <div className="relative mx-auto mt-5 w-fit">
            <span className="relative block">
              <Avatar name={hero.name} avatarKey={hero.avatarKey} size={96} ring="#fff" fill={teamAvatarFill(view.teams[teamOfSeat(r.deal.by)].color)} />
            </span>
            <span className="absolute -top-2 -right-3 text-4xl drop-shadow" aria-hidden>
              {run ? '🏃' : '🛡️'}
            </span>
          </div>
          <h2 id="bandh-result-h" className="mt-4 font-display text-3xl leading-tight font-extrabold">
            {run ? 'Bhaag gaya!' : 'Rok diya!'}
          </h2>
          <p className="mt-1 text-sm text-white/85">
            {run ? `${hero.name} ran on hand ${r.deal.atTrick} for ${running.name}.` : `${stopping.name} stopped ${running.name} — no run in 13 hands.`}
          </p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-black/25 px-4 py-1.5 font-display text-lg font-extrabold tabular-nums">
            {running.name} {signed(r.deal.points)}
          </p>
          <p className="mt-2 text-xs text-white/75 tabular-nums">
            {view.teams.A.name} {after('A')}/{b.limit} · {view.teams.B.name} {after('B')}/{b.limit}
          </p>
          {r.loser && <p className="mt-3 rounded-xl bg-white/15 px-3 py-2 text-sm font-extrabold">🏁 {view.teams[r.loser].name} reached {b.limit} — {view.teams[otherTeam(r.loser)].name} win!</p>}
          <button
            type="button"
            onClick={onClose}
            className="mt-5 block w-full rounded-xl bg-white/15 py-2.5 text-sm font-semibold text-white ring-1 ring-white/25 hover:bg-white/25"
          >
            Tap to continue
          </button>
        </div>
      </div>
    </dialog>
  );
}

function BandhGameOver({ view }: { view: RoomView }) {
  const b = view.bandh!;
  const loser = b.loser!;
  const winner = otherTeam(loser);
  return (
    <section className="card p-5 text-center" aria-label="Game over">
      <p className="eyebrow text-[10px] text-ink-soft">Game over</p>
      <p className="mt-1 font-display text-3xl font-extrabold" style={{ color: view.teams[winner].color }}>
        {view.teams[winner].name} win!
      </p>
      <p className="mt-1 text-sm text-ink-soft">
        {view.teams[loser].name} reached {b.limit} after {b.deals.length} deals.
      </p>
      <Link href="/" className="btn btn-ink mt-4 w-full">
        Leave table
      </Link>
    </section>
  );
}

function RunTracker({ view, hand }: { view: RoomView; hand: HandView }) {
  const team = view.teams[rangTeam(hand)];
  const runner = runChance(hand);
  const k = hand.tricks.length;
  // The hand being played (or the one that decided it).
  const shownHand = hand.outcome
    ? hand.outcome.by === 'calledOff' && hand.current.plays.length > 0
      ? hand.outcome.atTrick + 1
      : Math.max(hand.outcome.atTrick, 1)
    : Math.min(k + 1, TRICKS_PER_DEAL);
  const rang = rangTeam(hand);
  const dealing = view.teams[rang === 'A' ? 'B' : 'A'];
  let text: string;
  if (hand.outcome) {
    const { saved, by, decidedBy, atTrick } = hand.outcome;
    const who = view.seats[decidedBy]!.name;
    if (by === 'calledOff') {
      const when = hand.current.plays.length > 0 ? ` during hand ${atTrick + 1}` : atTrick > 0 ? ` after hand ${atTrick}` : '';
      text = `🏳️ ${who} called off the deal${when} — ${team.name} saved, next deal!`;
    }
    else if (by === 'run') text = `🏃 ${who} ran on trick ${atTrick} — ${team.name} saved!`;
    else if (saved) text = `🛟 No run, but ${who} took the last trick — ${team.name} saved!`;
    else text = `🛡️ ${dealing.name} stopped the run — ${team.name} get Guaa`;
  } else if (hand.phase === 'choosingTrump') {
    text = `${view.seats[hand.caller]!.name} is picking Rang from 5 cards…`;
  } else if (hand.phase === 'confirming') {
    text = `${view.seats[hand.dealer]!.name} (dealing) is checking their Rang cards — play on, or call off the deal?`;
  } else if (runner !== null) {
    text = `🔥 ${view.seats[runner]!.name} won trick ${k} — win this one (not with an Ace) and ${team.name} are saved! ${dealing.name}, stop them!`;
  } else if (k < RUN_FROM_TRICK - 1) {
    text = `Tricks 1–3 are mandatory. Then ${team.name} must run — ${dealing.name} try to stop them.`;
  } else {
    text = `${team.name} need a run (one player wins two in a row) or the last trick. ${dealing.name} must stop them.`;
  }
  if (view.variant === 'bandh') text = bandhTrackerText(view, hand, runner, k);
  return (
    <section className="panel px-3 py-2.5" aria-label="Run tracker">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="eyebrow text-[10px] text-cream/60">
          {hand.outcome?.by === 'calledOff'
            ? hand.current.plays.length > 0
              ? 'Called off during hand'
              : hand.outcome.atTrick > 0
                ? 'Called off after hand'
                : 'Called off — no hands played'
            : hand.outcome
              ? 'Decided on hand'
              : 'Hand'}
        </span>
        {(hand.outcome?.by !== 'calledOff' || hand.outcome.atTrick > 0 || hand.current.plays.length > 0) && (
          <span className="font-display text-lg leading-none font-extrabold tabular-nums" aria-label={`Hand ${shownHand} of ${TRICKS_PER_DEAL}`}>
            {shownHand}
            <span className="text-xs font-semibold text-cream/55"> / {TRICKS_PER_DEAL}</span>
          </span>
        )}
      </div>
      <ol className="flex items-center gap-1" aria-hidden>
        {Array.from({ length: TRICKS_PER_DEAL }, (_, i) => {
          const done = hand.tricks[i];
          const current = i === k && hand.phase === 'playing';
          return (
            <li key={i} className="flex flex-1 items-center gap-1">
              <span
                className={`grid h-4 flex-1 place-items-center rounded-full text-[9px] leading-none font-extrabold tabular-nums ${
                  done ? 'text-white/90' : current ? 'animate-pulse bg-gold text-ink' : i < RUN_FROM_TRICK - 1 ? 'bg-white/8 text-cream/35 ring-1 ring-white/15' : 'bg-white/12 text-cream/35'
                }`}
                style={done ? { background: view.teams[teamOfSeat(done.winner!)].color } : undefined}
              >
                {i + 1}
              </span>
              {i === RUN_FROM_TRICK - 2 && <span className="h-3 w-px bg-cream/30" />}
            </li>
          );
        })}
      </ol>
      <p className="mt-2 text-center text-xs font-semibold text-cream/90" aria-live="polite">
        {text}
      </p>
    </section>
  );
}

/**
 * The dealer, holding all 13 cards, decides whether their team can stop the run —
 * a popup with their cards right there, so it can't be missed or scrolled past.
 */
function DealerDecision({ cards, trump, rangTeam: rangName, onDecide }: { cards: Card[]; trump: Suit; rangTeam: string; onDecide: (play: boolean) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  const red = trump === 'H' || trump === 'D';
  return (
    <dialog
      ref={ref}
      aria-labelledby="decide-h"
      // The dealer has to choose; Esc doesn't just close it.
      onCancel={(e) => e.preventDefault()}
      className="m-auto w-[min(24rem,calc(100%-1.5rem))] max-w-none rounded-[1.75rem] bg-transparent p-0 backdrop:bg-black/65 backdrop:backdrop-blur-[2px]"
    >
      <div className="animate-pop rounded-[1.75rem] bg-cream p-4 text-ink shadow-[0_30px_70px_-20px_rgb(0_0_0/0.8)] ring-2 ring-gold/60">
        <p className="eyebrow text-center text-[10px] text-ink-soft">You dealt · all 13 cards are out</p>
        <h2 id="decide-h" className="mt-1 text-center font-display text-2xl font-extrabold">
          Play this deal?
        </h2>
        <p className="mt-1 text-center text-sm text-ink-soft">
          Rang is <span className={`font-extrabold ${red ? 'text-suit-red' : 'text-ink'}`}>{SUIT_SYMBOL[trump]}</span>. Can your team stop {rangName} from running?
        </p>
        <div className="mt-3 space-y-1.5 rounded-2xl bg-felt-950/90 px-2 py-2.5" aria-label="Your cards">
          {[cards.slice(0, 7), cards.slice(7)].map((row, r) => (
            <div key={r} className="flex justify-center -space-x-1">
              {row.map((c) => (
                <PlayingCard key={c} card={c} size="sm" trump={cardSuit(c) === trump} />
              ))}
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button type="button" className="btn btn-outline-ink h-14 flex-col gap-0 text-sm" onClick={() => onDecide(false)}>
            <span className="font-extrabold">🏳️ Call off</span>
            <span className="text-[10px] font-semibold text-ink-soft">{rangName} saved · next deal</span>
          </button>
          <button type="button" className="btn btn-ink h-14 flex-col gap-0 text-sm" onClick={() => onDecide(true)}>
            <span className="font-extrabold">▶ Play</span>
            <span className="text-[10px] font-semibold text-cream/70">Try to stop the run</span>
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** The caller's first 5 cards and the four suits, as a popup over the table. It closes only by choosing. */
function TrumpPicker({ cards, onPick, onRedeal }: { cards: Card[]; onPick: (s: Suit) => void; onRedeal?: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      aria-labelledby="pick-h"
      // Everyone is waiting on this choice; Esc doesn't just close it.
      onCancel={(e) => e.preventDefault()}
      className="m-auto max-h-[100dvh] w-[min(24rem,calc(100%-1.5rem))] max-w-none overflow-y-auto rounded-[1.75rem] bg-transparent p-0 backdrop:bg-black/60 backdrop:backdrop-blur-[2px] land:w-[min(30rem,calc(100%-1.5rem))]"
    >
      <div className="animate-pop rounded-[1.75rem] bg-cream p-4 text-ink shadow-[0_30px_70px_-20px_rgb(0_0_0/0.8)] ring-2 ring-gold/60 land:p-3">
        <p className="eyebrow text-center text-[10px] text-ink-soft">Your first 5 cards</p>
        <div className="mt-2 flex justify-center gap-1.5" aria-label="Your first 5 cards">
          {cards.map((c) => (
            <PlayingCard key={c} card={c} size="md" />
          ))}
        </div>
        <h2 id="pick-h" className="mt-4 text-center font-display text-2xl font-extrabold land:mt-2 land:text-xl">
          Choose Rang
        </h2>
        <p className="text-center text-xs text-ink-soft land:hidden">Everyone is waiting on you. Pick the trump suit.</p>
        <div className="mt-3 grid grid-cols-4 gap-2 land:mt-2 land:gap-1.5">
          {SUITS.map((s) => {
            const n = cards.filter((c) => cardSuit(c) === s).length;
            const red = s === 'H' || s === 'D';
            return (
              <button
                key={s}
                type="button"
                onClick={() => onPick(s)}
                className="flex flex-col items-center rounded-xl bg-white py-2.5 shadow-sm ring-2 ring-ink/10 transition hover:-translate-y-0.5 hover:ring-gold active:scale-95 land:flex-row land:justify-center land:gap-1.5 land:py-2"
                aria-label={`Choose ${SUIT_NAME[s]} as Rang — ${n} of your 5 cards`}
              >
                <span className={`text-3xl leading-none land:text-2xl ${red ? 'text-suit-red' : 'text-ink'}`}>{SUIT_SYMBOL[s]}</span>
                <span className="mt-1 text-[10px] font-bold text-ink-soft capitalize land:hidden">{SUIT_HINDI[s].roman}</span>
                <span
                  className={`mt-1 rounded-full px-2 py-px text-[11px] font-extrabold tabular-nums land:mt-0 ${n > 0 ? 'bg-ink text-cream' : 'bg-ink/8 text-ink-soft/60'}`}
                  aria-hidden
                >
                  {n}
                </span>
              </button>
            );
          })}
        </div>
        {onRedeal && (
          <div className="mt-3 rounded-xl bg-amber-100/70 p-3 text-center ring-1 ring-amber-400/40 land:mt-2 land:p-2">
            <p className="text-xs font-semibold text-ink">All 5 cards are below 10 — you can ask for a redeal.</p>
            <button type="button" onClick={onRedeal} className="btn btn-outline-ink mt-2 min-h-10 w-full text-sm">
              🔄 Ask for a redeal
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
}

interface DragState {
  card: Card;
  x: number;
  y: number;
  over: boolean;
}

function HandFan({
  cards,
  legal,
  myTurn,
  trump,
  onPlay,
  dropZone,
  onDropHover,
  rule = null,
}: {
  cards: Card[];
  legal: Card[];
  myTurn: boolean;
  trump: Suit | null;
  onPlay: (c: Card) => void;
  /** The table: releasing a dragged card over it plays the card. */
  dropZone?: RefObject<HTMLElement | null>;
  onDropHover?: (over: boolean) => void;
  /** Bandh Rang: why the cards are locked right now, if they are. */
  rule?: 'reveal' | 'rang' | null;
}) {
  const [ref, { width }] = useSize<HTMLDivElement>();
  const [selected, setSelected] = useState<Card | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const press = useRef<{ card: Card; x: number; y: number; moved: boolean } | null>(null);
  // A drag ends with a click on the card it started from; that click must not count as a tap.
  const suppressClick = useRef(false);
  const { w } = cardSize('md');
  const n = cards.length;
  // A little air wherever the suit changes, so same-coloured suits (♠ ♣, ♥ ♦) never blur together.
  const SUIT_GAP = 14;
  const breaks = cards.filter((c, i) => i > 0 && cardSuit(c) !== cardSuit(cards[i - 1]!)).length;
  const step = n > 1 ? Math.min(42, (width - w - breaks * SUIT_GAP) / (n - 1)) : 0;
  useEffect(() => {
    if (!myTurn || (selected && !cards.includes(selected))) setSelected(null);
  }, [myTurn, cards, selected]);
  useEffect(() => {
    if (!myTurn && press.current) {
      press.current = null;
      setDrag(null);
      onDropHover?.(false);
    }
  }, [myTurn, onDropHover]);

  const play = (c: Card) => {
    setSelected(null);
    tick();
    onPlay(c);
  };

  const tap = (c: Card) => {
    unlockAudio();
    if (suppressClick.current) return;
    if (!myTurn) return;
    if (!legal.includes(c)) {
      if (rule === 'reveal') return void toast('You can’t follow suit — tap 🔓 Reveal Rang first.');
      if (rule === 'rang') return void toast('You revealed the Rang — play a Rang card.');
      const led = legal[0] ? SUIT_NAME[cardSuit(legal[0])] : '';
      toast(`Follow suit — play one of your ${led}.`);
      return;
    }
    if (selected === c) play(c);
    else setSelected(c);
  };

  /** Over the felt (not the strip at the bottom where your own badge sits)? */
  const overTable = (x: number, y: number) => {
    const r = dropZone?.current?.getBoundingClientRect();
    return Boolean(r && x >= r.left && x <= r.right && y >= r.top + r.height * 0.15 && y <= r.bottom - r.height * 0.08);
  };

  const onPointerDown = (c: Card) => (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!myTurn || !legal.includes(c) || e.button !== 0) return;
    press.current = { card: c, x: e.clientX, y: e.clientY, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    if (!p) return;
    if (!p.moved && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 10) {
      p.moved = true;
      unlockAudio();
    }
    if (!p.moved) return;
    const over = overTable(e.clientX, e.clientY);
    setDrag({ card: p.card, x: e.clientX, y: e.clientY, over });
    onDropHover?.(over);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    press.current = null;
    if (!p?.moved) return;
    suppressClick.current = true;
    setTimeout(() => (suppressClick.current = false), 0);
    setDrag(null);
    onDropHover?.(false);
    if (overTable(e.clientX, e.clientY)) play(p.card);
  };
  const onPointerCancel = () => {
    press.current = null;
    setDrag(null);
    onDropHover?.(false);
  };

  return (
    <div ref={ref} className="relative w-full" aria-label="Your cards">
      <div className="flex h-[104px] items-end justify-center pt-5">
        {cards.map((c, i) => {
          const playable = myTurn && legal.includes(c);
          const dragging = drag?.card === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => tap(c)}
              onPointerDown={onPointerDown(c)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              aria-label={`${cardName(c)}${playable ? '' : myTurn ? ' (must follow suit)' : ''}${selected === c ? ', tap again to play' : ''}`}
              aria-pressed={selected === c}
              className={`relative transition-[transform,opacity] duration-150 ease-out ${playable ? 'touch-none' : ''}`}
              style={{
                marginLeft: i === 0 ? 0 : step - w + (cardSuit(c) !== cardSuit(cards[i - 1]!) ? SUIT_GAP : 0),
                transform: `translateY(${selected === c ? -20 : playable ? -6 : 0}px)`,
                opacity: dragging ? 0.25 : 1,
                zIndex: i,
              }}
            >
              <PlayingCard
                card={c}
                size="md"
                trump={cardSuit(c) === trump}
                dim={myTurn && !playable}
                style={
                  i > 0 && cardSuit(c) !== cardSuit(cards[i - 1]!) && cardSuit(c) !== trump
                    ? { boxShadow: '-6px 0 10px -4px rgb(0 0 0 / 0.55), 0 0 0 1px rgb(28 25 23 / 0.12), 0 6px 14px -6px rgb(0 0 0 / 0.55)' }
                    : undefined
                }
              />
            </button>
          );
        })}
      </div>
      <p className="mt-1 h-4 text-center text-[11px] font-semibold text-gold-soft" aria-live="polite">
        {drag
          ? drag.over
            ? `Let go to play ${cardName(drag.card)}`
            : 'Drag onto the table'
          : selected
            ? `Tap ${cardName(selected)} again to play it`
            : myTurn
              ? rule === 'reveal'
                ? 'Your turn — reveal the Rang to play'
                : rule === 'rang'
                  ? 'Your turn — you revealed the Rang, so play a Rang card'
                  : 'Your turn — drag a card to the table, or tap it twice'
              : ''}
      </p>
      {drag && (
        <span
          className="pointer-events-none fixed z-50 transition-transform duration-75"
          style={{ left: drag.x, top: drag.y, transform: `translate(-50%, -62%) rotate(${drag.over ? 0 : -6}deg) scale(${drag.over ? 1.12 : 1.05})` }}
          aria-hidden
        >
          <PlayingCard
            card={drag.card}
            size="md"
            trump={cardSuit(drag.card) === trump}
            style={{ boxShadow: drag.over ? '0 0 0 3px #ecd48f, 0 18px 36px -10px rgb(0 0 0 / 0.8)' : '0 18px 36px -10px rgb(0 0 0 / 0.75)' }}
          />
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

export function GameTable({ view, conn }: { view: RoomView; conn: RoomConnection }) {
  const me = view.you;
  const viewer: Seat = me ?? 2;
  // Bandh Rang: until it's revealed, nothing on screen shows the Rang — not even on the chooser's own
  // phone (the tile offers them a private peek instead), so no one can read it off a glance.
  const secretRang = view.variant === 'bandh' && view.hand && !view.hand.rangRevealed ? view.hand.trump : null;
  const hand = view.hand && secretRang ? { ...view.hand, trump: null } : view.hand;
  const state: MatchState = { rules: view.rules, firstDealer: view.firstDealer, deals: view.deals };
  const deal = useDealAnimation(view);
  const held = useHeldTrick(hand);
  const revealSuit = useTrumpReveal(view);
  const [celebration, setCelebration] = useState<SavedCelebration | null>(null);
  const [bandhResult, setBandhResult] = useState<BandhResult | null>(null);
  useHandResult(view, state, setCelebration, setBandhResult);
  useRedealNotice(view);

  const shownCount = (seat: Seat) => deal.shown?.[seat] ?? hand?.counts[seat] ?? 0;
  const trick = held ?? (hand?.current.plays.length ? hand.current : null);
  const turnSeat = hand && !held && !deal.dealing ? hand.toAct : null;
  // Which hand of the deal the cards on the felt belong to (a just-won one while it's held).
  const trickNo = hand ? (held ? hand.tricks.length - 1 : hand.tricks.length) : 0;
  const [tableRef, table] = useSize<HTMLElement>();
  const [dropHover, setDropHover] = useState(false);
  const runner = hand && !held ? runChance(hand) : null;
  const myTurn = me !== null && turnSeat === me;
  // Bandh Rang: must reveal before playing; then (that one card) must play Rang if held.
  const ledNow = hand?.current.plays[0] ? cardSuit(hand.current.plays[0].card) : null;
  const bandhRule: 'reveal' | 'rang' | null =
    view.variant !== 'bandh' || !myTurn || !hand
      ? null
      : hand.canReveal
        ? 'reveal'
        : hand.rangRevealed && hand.trump && ledNow !== hand.trump && hand.legal.length > 0 && hand.legal.every((c) => cardSuit(c) === hand.trump) && hand.myCards.some((c) => cardSuit(c) !== hand.trump)
          ? 'rang'
          : null;
  useYourTurnNudge(myTurn);

  let myCards: Card[] = [];
  if (hand && me !== null) {
    myCards = deal.shown ? hand.myCards.slice(0, deal.shown[me]) : sortHand(hand.myCards, hand.trump);
  }

  const dealerName = (s: Seat | null) => (s === null ? '' : view.seats[s]!.name);
  /** What the middle of the felt says: who (in the display face) and what they're doing (a gold caps label). */
  const centerText = ((): { who?: string; action: string } | null => {
    if (deal.shuffling) return { who: dealerName(hand?.dealer ?? null), action: 'is shuffling' };
    if (trick) return null;
    if (view.phase === 'ready') return { who: dealerName(view.nextDealer), action: 'to shuffle & deal' };
    if (view.phase === 'over') return { action: 'Round complete' };
    if (!hand) return null;
    if (hand.phase === 'choosingTrump') return deal.dealing ? { action: 'Dealing' } : { who: dealerName(hand.caller), action: 'is choosing Rang' };
    if (hand.phase === 'confirming' && !deal.dealing) return { who: dealerName(hand.dealer), action: 'play or call off?' };
    // Your own turn is already called out under your cards (and the arrow runs where this would sit).
    if (hand.phase === 'playing' && !deal.dealing) return hand.toAct === me ? null : { who: dealerName(hand.toAct), action: 'to lead' };
    return null;
  })();

  // Whose decision the whole table is waiting on, shown with their photo in the middle.
  const waitingOn: Seat | null =
    view.phase === 'ready'
      ? view.nextDealer
      : hand && !deal.dealing && !trick
        ? hand.phase === 'choosingTrump'
          ? hand.caller
          : hand.phase === 'confirming'
            ? hand.dealer
            : null
        : null;
  const matchOver = view.phase === 'over';
  const status = matchStatus(state);

  // Portrait: one column (order-* keeps the reading order). Landscape phone: the table on the left at full
  // height; score, controls and your cards in a column on the right.
  return (
    <div className="flex flex-col gap-3 land:grid land:h-[calc(100dvh-4.5rem)] land:grid-cols-[minmax(0,1fr)_minmax(17rem,22rem)] land:grid-rows-[auto_minmax(0,1fr)_auto] land:gap-x-4 land:gap-y-2 land:[grid-template-areas:'table_score''table_side''table_hand']">
      <div className="order-1 land:[grid-area:score]">{view.bandh ? <BandhScoreStrip view={view} /> : <ScoreStrip view={view} />}</div>

      <section
        ref={tableRef}
        className="relative order-2 mx-auto h-[27rem] w-full max-w-md select-none land:h-full land:min-h-0 land:max-w-none land:[grid-area:table]"
        aria-label="Card table"
      >
        <Felt width={table.width} height={table.height} glow={dropHover} />
        {hand && !deal.shuffling && <HandCountTile hand={hand} trickNo={trickNo} />}
        {hand && !deal.shuffling && <RangTile key={`${view.handId}-${hand.trump ?? 'choosing'}`} hand={hand} name={dealerName(hand.caller)} peek={secretRang} />}
        {turnSeat !== null && <TurnArrow pos={posOf(turnSeat, viewer)} width={table.width} height={table.height} />}

        {deal.shuffling && <ShuffleDeck />}
        {trick?.plays.map((p, i) => (
          <TrickCard key={`${view.handId}-${p.seat}-${p.card === HIDDEN_CARD ? `down${trickNo}` : p.card}`} card={p.card} faceDown={Boolean(p.faceDown)} pos={posOf(p.seat, viewer)} trump={view.variant === 'bandh' ? (p.live ? cardSuit(p.card) : null) : (hand?.trump ?? null)} winning={held !== null && held.winner === p.seat} lead={i === 0} size={table} />
        ))}
        {held && (
          <span className="absolute top-[71%] left-1/2 z-30 -translate-x-1/2 rounded-full bg-gold px-2.5 py-0.5 text-[11px] font-extrabold whitespace-nowrap text-ink shadow-lg">
            {dealerName(held.winner!)} wins the trick
          </span>
        )}
        {centerText && !deal.shuffling && (
          <div
            className="absolute left-1/2 z-10 w-[44%] -translate-x-1/2 -translate-y-1/2 text-center"
            style={{ top: `${hand?.phase === 'playing' ? FELT_Y + FELT_RY * 0.72 : FELT_Y}%` }}
          >
            <FeltCaption {...centerText} />
          </div>
        )}
        {waitingOn !== null && centerText && !deal.shuffling && <PointerArrow pos={posOf(waitingOn, viewer)} />}
        {deal.shuffling && centerText && (
          <div className="absolute top-[76%] left-1/2 z-10 -translate-x-1/2 text-center whitespace-nowrap">
            <FeltCaption {...centerText} />
          </div>
        )}

        {([0, 1, 2, 3] as Seat[])
          .filter((s) => s !== me)
          .map((s) => (
            <SeatView key={s} view={view} seat={s} pos={posOf(s, viewer)} cards={shownCount(s)} turn={turnSeat === s} run={runner === s} />
          ))}
        {me !== null && <MeBadge view={view} me={me} turn={myTurn} run={runner === me} />}
        {view.variant !== 'bandh' && hand && canCallOff(hand, me) && (
          <button
            type="button"
            className="absolute top-[85%] right-0 z-20 flex -translate-y-1/2 items-center gap-1.5 rounded-full py-1.5 pr-3 pl-2 text-xs font-extrabold text-white shadow-[0_8px_20px_-8px_rgb(190_18_60/0.8)] ring-1 ring-white/30 transition hover:brightness-110 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #f43f5e 0%, #be123c 100%)' }}
            onClick={async () => {
              const rangName = view.teams[rangTeam(hand)].name;
              const ok = await confirmDialog({
                title: 'Call off this deal?',
                icon: '🏳️',
                body: `${rangName} will be saved (0 Guaa) and you move on to the next deal. Agree with your partner first.`,
                confirmLabel: 'Call off deal',
                tone: 'danger',
              });
              if (ok) conn.send({ t: 'decide', play: false });
            }}
            aria-label="Call off this deal"
          >
            <span className="grid h-5 w-5 place-items-center rounded-full bg-white/20 text-[11px]" aria-hidden>
              🏳️
            </span>
            Call off
          </button>
        )}

        {deal.flights.map((f) => (
          <FlyingCard key={f.id} from={posOf(f.from, viewer)} to={posOf(f.to, viewer)} />
        ))}

        {revealSuit && (
          <div className="pointer-events-none absolute inset-0 z-40 grid place-items-center" aria-live="assertive">
            <div className="animate-reveal rounded-2xl bg-cream px-6 py-3 text-center text-ink shadow-[0_20px_50px_-10px_rgb(0_0_0/0.8)] ring-4 ring-gold">
              <p className="eyebrow text-[10px] text-ink-soft">{view.variant === 'bandh' ? 'Rang revealed' : `${dealerName(hand?.caller ?? null)} chose`}</p>
              <p className="font-display text-3xl font-extrabold">
                Rang <span className={revealSuit === 'H' || revealSuit === 'D' ? 'text-suit-red' : ''}>{SUIT_SYMBOL[revealSuit]}</span>
              </p>
              <p className="text-xs font-semibold text-ink-soft">
                {SUIT_NAME[revealSuit]} · <span className="capitalize">{SUIT_HINDI[revealSuit].roman}</span> — beats every other suit
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Controls: in portrait they flow in reading order; in landscape they stack in the right column. */}
      <div className="contents land:flex land:min-h-0 land:flex-col land:gap-2 land:overflow-y-auto land:[grid-area:side]">
      {hand?.phase === 'choosingTrump' && me === hand.caller && !deal.dealing && (
        <div className="order-3">
        <TrumpPicker
          cards={sortHand(hand.myCards, null)}
          onPick={(suit) => {
            unlockAudio();
            conn.send({ t: 'trump', suit });
          }}
          onRedeal={hand.canRedeal ? () => conn.send({ t: 'redeal' }) : undefined}
        />
        </div>
      )}
      {hand?.canReveal && (
        <div className="order-4">
        <div className="animate-rise flex items-center justify-between gap-3 rounded-2xl bg-felt-950/70 px-3 py-2 ring-1 ring-gold/40">
          <p className="text-xs font-semibold text-cream/85">You can’t follow suit — reveal the Rang to play. Then you must play a Rang card if you have one.</p>
          <button
            type="button"
            className="btn btn-primary min-h-10 shrink-0 px-3 text-sm"
            onClick={() => {
              unlockAudio();
              conn.send({ t: 'reveal' });
            }}
          >
            🔓 Reveal Rang
          </button>
        </div>
        </div>
      )}
      {hand && hand.phase !== 'choosingTrump' && (
        <div className="order-6">
          <RunTracker view={view} hand={hand} />
        </div>
      )}
      {view.phase === 'ready' && (
        <div className="order-7 pt-1 land:pt-0">
          {me !== null && me === view.nextDealer ? (
            <button
              type="button"
              className="btn btn-primary h-16 w-full flex-col gap-0.5 text-lg"
              onClick={() => {
                unlockAudio();
                conn.send({ t: 'deal' });
              }}
            >
              <span className="font-extrabold">🃏 Shuffle & deal</span>
              <span className="text-xs font-semibold text-ink/65">
                {view.variant === 'bandh'
                  ? `5 cards to ${dealerName(view.nextDealer === null ? null : nextSeat(view.nextDealer, view.rules.turnDirection))} first, for a hidden Rang`
                  : `5 cards to ${dealerName(view.nextDealer === null ? null : nextSeat(view.nextDealer, view.rules.turnDirection))} first, for Rang`}
              </span>
            </button>
          ) : (
            <p className="panel p-3 text-center text-sm text-cream/85">Waiting for {dealerName(view.nextDealer)} to shuffle & deal…</p>
          )}
        </div>
      )}
      {matchOver && view.bandh && (
        <div className="order-8">
          <BandhGameOver view={view} />
        </div>
      )}
      {matchOver && !view.bandh && (
        <section className="order-8 card p-4 text-center" aria-label="Round complete">
          <p className="font-display text-2xl font-extrabold">
            {status.leader ? `${view.teams[status.leader].name} win!` : "It's a tie!"}
          </p>
          <p className="text-sm text-ink-soft">Fewest Guaa wins.</p>
          <div className="mt-3 grid gap-2">
            {me !== null && (
              <button type="button" className="btn btn-ink" onClick={() => conn.send({ t: 'more', hands: view.rules.dealsPerTurn })}>
                Keep playing · {view.rules.dealsPerTurn} more each
              </button>
            )}
            <Link href="/" className="btn btn-outline-ink">
              Leave table
            </Link>
          </div>
        </section>
      )}
      </div>

      <div className="contents land:block land:min-w-0 land:[grid-area:hand]">
      {me !== null && hand && (hand.phase === 'playing' || hand.phase === 'confirming') && myCards.length > 0 && (
        <div className="order-5">
        <HandFan
          cards={myCards}
          legal={hand.legal}
          myTurn={myTurn}
          trump={hand.trump}
          onPlay={(card) => conn.send({ t: 'play', card })}
          dropZone={tableRef}
          onDropHover={setDropHover}
          rule={bandhRule}
        />
        </div>
      )}
      {me !== null && hand?.phase === 'choosingTrump' && me === hand.caller && myCards.length > 0 && (
        // Only while the 5 are being dealt; after that they're in the Choose Rang popup.
        <div className={`order-5 ${deal.dealing ? '' : 'hidden'}`}>
        <HandFan cards={myCards} legal={[]} myTurn={false} trump={null} onPlay={() => {}} />
        </div>
      )}
      </div>

      {hand?.phase === 'confirming' && me === hand.dealer && !deal.dealing && (
        <DealerDecision
          cards={sortHand(hand.myCards, hand.trump)}
          trump={hand.trump!}
          rangTeam={view.teams[rangTeam(hand)].name}
          onDecide={(play) => {
            unlockAudio();
            conn.send({ t: 'decide', play });
          }}
        />
      )}
      {celebration && <SavedPopup key={celebration.key} view={view} c={celebration} onClose={() => setCelebration(null)} />}
      {bandhResult && <BandhResultPopup key={bandhResult.key} view={view} r={bandhResult} onClose={() => setBandhResult(null)} />}



    </div>
  );
}

function MeBadge({ view, me, turn, run }: { view: RoomView; me: Seat; turn: boolean; run: boolean }) {
  const p = view.seats[me]!;
  const team = view.teams[teamOfSeat(me)];
  const hand = view.hand;
  const isDealer = (hand?.dealer ?? view.nextDealer) === me;
  const isCaller = hand?.caller === me;
  const won = hand?.tricks.filter((t) => t.winner === me).length ?? 0;
  return (
    <div
      className="absolute z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full bg-felt-950/70 py-1 pr-3 pl-1 ring-1 ring-white/10"
      style={{ left: `${ANCHOR.bottom.x}%`, top: `${ANCHOR.bottom.y}%` }}
    >
      <span className={`block rounded-full ${turn ? 'turn-glow' : ''}`}>
        <Avatar name={p.name} avatarKey={p.avatarKey} size={30} ring={team.color} fill={teamAvatarFill(team.color)} />
      </span>
      <span className="text-xs font-bold">
        You{run ? ' 🔥' : ''}
        {won > 0 && <span className="ml-1 font-semibold text-cream/70">· {won} won</span>}
      </span>
      {(isDealer || isCaller) && (
        <span className={`rounded-full px-1.5 py-px text-[8px] font-extrabold tracking-wider ${isDealer ? 'bg-gold text-ink' : 'bg-cream text-ink'}`}>
          {isDealer ? 'DEALER' : <RangChipLabel hand={hand ?? null} />}
        </span>
      )}
    </div>
  );
}
